import { useEffect, useState } from "react";
import axios from "axios";
import { API, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, Star, Trash, Download, ArrowSquareOut, Calendar, X, Sparkle, Robot, Trophy } from "@phosphor-icons/react";

const STATUS_COLORS = {
  new: "#DFFF00",
  reviewed: "#00E5FF",
  shortlisted: "#FF4500",
  interviewing: "#00FFB2",
  rejected: "#8E8E93",
};

const STATUSES = ["new", "reviewed", "shortlisted", "interviewing", "rejected"];

const VERDICT_COLOR = {
  strong_yes: "#00FFB2",
  yes: "#DFFF00",
  maybe: "#00E5FF",
  no: "#FF3060",
};

/**
 * Admin panel showing every candidate application submitted through the
 * in-app apply flow (POST /positions/:id/apply).  Actions per row:
 *   • change status (new → reviewed → shortlisted → rejected)
 *   • open resume in a new tab
 *   • delete
 */
export default function ApplicationsPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [scheduling, setScheduling] = useState(null); // application being scheduled
  const [screeningId, setScreeningId] = useState(null); // app currently being screened by AI
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardOpen, setLeaderboardOpen] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/applications`, { headers: { "X-Admin-Token": token } });
      setRows(data || []);
    } catch (e) {
      toast.error("Couldn't load applications.");
    } finally {
      setLoading(false);
    }
    // Fetch leaderboard in parallel (best-effort)
    try {
      const { data } = await axios.get(`${API}/admin/applications/leaderboard?limit=10`, { headers: { "X-Admin-Token": token } });
      setLeaderboard(data.leaderboard || []);
    } catch { /* silent — leaderboard is a nice-to-have */ }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/applications/${id}/status`, { status }, { headers: { "X-Admin-Token": token } });
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, status } : r));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Failed to update status."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await axios.post(`${API}/admin/applications/${id}/delete`, {}, { headers: { "X-Admin-Token": token } });
      setRows((rs) => rs.filter((r) => r.id !== id));
      if (open?.id === id) setOpen(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed."); }
  };

  const aiScreen = async (id) => {
    setScreeningId(id);
    try {
      const { data } = await axios.post(`${API}/admin/applications/${id}/ai-screen`, {}, { headers: { "X-Admin-Token": token } });
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, ai_screen: data.ai_screen } : r));
      setOpen((o) => o && o.id === id ? { ...o, ai_screen: data.ai_screen } : o);
      const verdict = data.ai_screen?.verdict || "—";
      const score = data.ai_screen?.fit_score ?? "—";
      toast.success(`AI screen · ${verdict.toUpperCase()} · fit ${score}/100`);
      // Refresh leaderboard so the newly-screened candidate lands in the top 10
      try {
        const { data: lb } = await axios.get(`${API}/admin/applications/leaderboard?limit=10`, { headers: { "X-Admin-Token": token } });
        setLeaderboard(lb.leaderboard || []);
      } catch { /* silent */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI screen failed.");
    } finally {
      setScreeningId(null);
    }
  };

  const exportCsv = () => {
    const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);
    const header = ["date", "position", "name", "email", "phone", "status", "resume_url"];
    const csv = [header.join(",")].concat(
      filtered.map((r) => [
        r.created_at || "",
        `"${(r.position_title || "").replace(/"/g, '""')}"`,
        `"${(r.full_name || "").replace(/"/g, '""')}"`,
        r.email || "",
        r.phone || "",
        r.status || "",
        r.resume_url || "",
      ].join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wisewit-applications-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div data-testid="applications-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-overline mb-2">◢ Applications ({rows.length})</div>
          <div className="text-[#8E8E93] text-sm">Every candidate who applied through wisewit.ai/careers.</div>
        </div>
        <div className="flex items-center gap-2">
          {["all", "new", "reviewed", "shortlisted", "interviewing", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`apps-filter-${s}`}
              className={`text-[10px] font-mono tracking-[0.18em] uppercase border px-3 py-1.5 ${filter === s ? "border-[#DFFF00] text-[#DFFF00]" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`}
            >
              {s} {s !== "all" && `(${rows.filter((r) => r.status === s).length})`}
            </button>
          ))}
          <button onClick={exportCsv} className="btn-ghost text-[10px] py-2 px-3" data-testid="apps-export"><Download size={12} /> CSV</button>
        </div>
      </div>

      {/* Top 10 leaderboard by AI fit score */}
      {leaderboard.length > 0 && (
        <div className="border border-[#DFFF00]/40 bg-[#DFFF00]/[0.04] mb-6" data-testid="apps-leaderboard">
          <button
            onClick={() => setLeaderboardOpen(!leaderboardOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#DFFF00]/[0.06]"
            data-testid="apps-leaderboard-toggle"
          >
            <div className="flex items-center gap-2">
              <Trophy size={16} color="#DFFF00" weight="fill" />
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">
                Top {leaderboard.length} by AI fit score
              </div>
              <div className="text-[#8E8E93] text-sm">
                — your first glance every morning
              </div>
            </div>
            <div className="font-mono text-[10px] text-[#8E8E93]">
              {leaderboardOpen ? "− collapse" : "+ expand"}
            </div>
          </button>

          {leaderboardOpen && (
            <div className="border-t border-[#2A2A2A]" data-testid="apps-leaderboard-rows">
              {leaderboard.map((r, i) => {
                const score = r.ai_screen?.fit_score ?? 0;
                const verdict = r.ai_screen?.verdict || "maybe";
                const verdictColor = VERDICT_COLOR[verdict] || "#8E8E93";
                return (
                  <div
                    key={r.id}
                    className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#2A2A2A] last:border-b-0 hover:bg-[#DFFF00]/[0.03] items-center"
                  >
                    <div className="col-span-1 font-mono text-lg text-[#DFFF00]">#{i + 1}</div>
                    <div className="col-span-3">
                      <button
                        onClick={() => setOpen(r)}
                        className="text-white hover:text-[#DFFF00] text-left"
                        data-testid={`lb-${r.id}-open`}
                      >
                        {r.full_name}
                      </button>
                      <div className="text-[#8E8E93] font-mono text-[10px]">{r.email}</div>
                    </div>
                    <div className="col-span-3 text-[#D7D7D9] text-xs truncate" title={r.position_title}>
                      {r.position_title || "—"}
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#1A1A1A]">
                          <div className="h-full" style={{ width: `${score}%`, background: verdictColor }} />
                        </div>
                        <span className="font-mono text-xs" style={{ color: verdictColor }}>{score}</span>
                      </div>
                    </div>
                    <div className="col-span-1 font-mono text-[10px] tracking-[0.16em] uppercase" style={{ color: verdictColor }}>
                      {verdict.replace("_", " ")}
                    </div>
                    <div className="col-span-2 flex justify-end gap-2">
                      <button
                        onClick={() => setScheduling(r)}
                        className="p-1.5 border border-[#2A2A2A] hover:border-[#00FFB2]"
                        title="Schedule interview"
                        data-testid={`lb-${r.id}-schedule`}
                      >
                        <Calendar size={12} color="#00FFB2" />
                      </button>
                      <button
                        onClick={() => changeStatus(r.id, "shortlisted")}
                        className="p-1.5 border border-[#2A2A2A] hover:border-[#FF4500]"
                        title="Shortlist"
                        data-testid={`lb-${r.id}-shortlist`}
                      >
                        <Star size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading && <div className="text-[#8E8E93] font-mono text-[11px] tracking-[0.18em] uppercase">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="border border-[#2A2A2A] px-4 py-10 text-[#555] text-sm text-center">No applications {filter !== "all" ? `with status "${filter}"` : "yet"}.</div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="border border-[#2A2A2A]">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
            <div className="col-span-2">Date</div>
            <div className="col-span-3">Candidate</div>
            <div className="col-span-3">Role</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filtered.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] text-sm items-center">
              <div className="col-span-2 font-mono text-[11px] text-[#8E8E93]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
              <div className="col-span-3">
                <button onClick={() => setOpen(r)} className="text-white hover:text-[#DFFF00] block text-left" data-testid={`app-${r.id}-open`}>{r.full_name}</button>
                <div className="text-[#8E8E93] font-mono text-[10px]">{r.email}</div>
              </div>
              <div className="col-span-3 text-[#D7D7D9] truncate" title={r.position_title}>{r.position_title || r.position_id}</div>
              <div className="col-span-2 font-mono text-[10px] tracking-[0.22em] uppercase" style={{ color: STATUS_COLORS[r.status] || "#8E8E93" }}>{r.status}</div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <a href={mediaUrl(r.resume_url)} target="_blank" rel="noreferrer" className="p-2 border border-[#2A2A2A] hover:border-[#DFFF00]" title="Open resume" data-testid={`app-${r.id}-resume`}><ArrowSquareOut size={12} /></a>
                <button onClick={() => aiScreen(r.id)} disabled={screeningId === r.id} className="p-2 border border-[#2A2A2A] hover:border-[#DFFF00] relative" title={r.ai_screen ? `AI-screened · ${r.ai_screen.verdict} · ${r.ai_screen.fit_score}/100` : "AI screen"} data-testid={`app-${r.id}-aiscreen`}>
                  {screeningId === r.id ? <Sparkle size={12} className="animate-spin" color="#DFFF00" /> : <Robot size={12} color={r.ai_screen ? "#DFFF00" : "#8E8E93"} weight={r.ai_screen ? "fill" : "regular"} />}
                  {r.ai_screen && <span className="absolute -top-1 -right-1 bg-[#DFFF00] text-black text-[8px] font-bold px-1 leading-none py-0.5">{r.ai_screen.fit_score}</span>}
                </button>
                <button onClick={() => setScheduling(r)} className="p-2 border border-[#2A2A2A] hover:border-[#00FFB2]" title="Schedule interview" data-testid={`app-${r.id}-schedule`}><Calendar size={12} color="#00FFB2" /></button>
                <button onClick={() => changeStatus(r.id, "shortlisted")} className="p-2 border border-[#2A2A2A] hover:border-[#FF4500]" title="Shortlist" data-testid={`app-${r.id}-shortlisted`}><Star size={12} /></button>
                <button onClick={() => changeStatus(r.id, "reviewed")} className="p-2 border border-[#2A2A2A] hover:border-[#00E5FF]" title="Mark reviewed" data-testid={`app-${r.id}-reviewed`}><CheckCircle size={12} /></button>
                <button onClick={() => remove(r.id)} className="p-2 border border-[#2A2A2A] hover:border-[#FF3060]" title="Delete" data-testid={`app-${r.id}-delete`}><Trash size={12} color="#FF3060" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#050505]/80 backdrop-blur flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={() => setOpen(null)}>
          <div className="max-w-2xl w-full bg-[#0a0a0a] border border-[#2A2A2A] p-6" onClick={(e) => e.stopPropagation()} data-testid="app-detail-modal">
            <div className="text-overline mb-2">◢ Application detail</div>
            <h3 className="font-display text-2xl text-white">{open.full_name}</h3>
            <div className="mt-1 font-mono text-xs text-[#DFFF00]">{open.email}{open.phone ? ` · ${open.phone}` : ""}</div>
            <div className="mt-1 font-mono text-[10px] text-[#8E8E93] tracking-[0.22em] uppercase">◢ {open.position_title}</div>

            <a href={mediaUrl(open.resume_url)} target="_blank" rel="noreferrer" className="btn-secondary mt-6 inline-flex" data-testid="app-detail-open-resume">
              <ArrowSquareOut size={12} /> Open resume
            </a>

            {open.cover_message && (
              <div className="mt-6 border-t border-[#2A2A2A] pt-4">
                <div className="text-overline mb-2">◢ Cover message</div>
                <p className="text-[#D7D7D9] whitespace-pre-wrap text-sm">{open.cover_message}</p>
              </div>
            )}

            {open.answers?.length > 0 && (
              <div className="mt-6 border-t border-[#2A2A2A] pt-4 space-y-4">
                <div className="text-overline">◢ Answers</div>
                {open.answers.map((a, i) => (
                  <div key={i}>
                    <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93]">Q{i + 1} · {a.q}</div>
                    <div className="mt-1 text-[#D7D7D9] whitespace-pre-wrap text-sm">{a.a || "—"}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 border-t border-[#2A2A2A] pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-overline">◢ AI screen</div>
                <button
                  onClick={() => aiScreen(open.id)}
                  disabled={screeningId === open.id}
                  className="btn-secondary text-[10px] py-1 px-3"
                  data-testid="app-detail-aiscreen"
                >
                  <Robot size={12} /> {screeningId === open.id ? "Screening…" : (open.ai_screen ? "Re-run AI screen" : "Run AI screen")}
                </button>
              </div>
              {open.ai_screen ? (
                <AIScreenBlock screen={open.ai_screen} />
              ) : (
                <div className="text-[#8E8E93] text-xs">No AI screen yet. Click above to run a GPT-5.2 fit assessment against the role.</div>
              )}
            </div>

            <div className="mt-8 pt-4 border-t border-[#2A2A2A] flex flex-wrap gap-2 justify-end">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => changeStatus(open.id, s)}
                  className={`text-[10px] font-mono tracking-[0.18em] uppercase border px-3 py-2 ${open.status === s ? "border-[#DFFF00] text-[#DFFF00]" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`}
                >
                  {s}
                </button>
              ))}
              <button onClick={() => { setScheduling(open); setOpen(null); }} className="btn-secondary text-[10px] py-2 px-3" data-testid="app-detail-schedule">
                <Calendar size={12} /> Schedule interview
              </button>
              <button onClick={() => setOpen(null)} className="btn-primary">Close</button>
            </div>
          </div>
        </div>
      )}

      {scheduling && (
        <ScheduleInterviewModal
          application={scheduling}
          token={token}
          onDone={(updatedStatus) => {
            setRows((rs) => rs.map((r) => r.id === scheduling.id ? { ...r, status: updatedStatus } : r));
            setScheduling(null);
          }}
          onClose={() => setScheduling(null)}
        />
      )}
    </div>
  );
}


function AIScreenBlock({ screen }) {
  const verdict = screen?.verdict || "maybe";
  const color = VERDICT_COLOR[verdict] || "#8E8E93";
  const score = screen?.fit_score ?? 0;
  return (
    <div className="border border-[#2A2A2A] p-4 bg-[#0a0a0a]" data-testid="ai-screen-block">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8E8E93]">Verdict</div>
          <div className="mt-1 font-mono text-lg" style={{ color }} data-testid="ai-screen-verdict">
            {verdict.replace("_", " ").toUpperCase()}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8E8E93]">Fit score</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="w-24 h-2 bg-[#1A1A1A]"><div className="h-full" style={{ width: `${score}%`, background: color }} /></div>
            <span className="font-mono text-sm" style={{ color }} data-testid="ai-screen-score">{score}/100</span>
          </div>
        </div>
        {screen.screened_at && (
          <div className="text-[10px] text-[#555] font-mono ml-auto">Ran: {new Date(screen.screened_at).toLocaleString()}</div>
        )}
      </div>

      {screen.highlights?.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00FFB2] mb-1">Highlights</div>
          <ul className="text-[#D7D7D9] text-sm list-disc pl-5 space-y-1">
            {screen.highlights.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
      )}
      {screen.concerns?.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF3060] mb-1">Concerns</div>
          <ul className="text-[#D7D7D9] text-sm list-disc pl-5 space-y-1">
            {screen.concerns.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
      {screen.suggested_next_step && (
        <div className="mt-4 border-t border-[#2A2A2A] pt-3">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00] mb-1">Suggested next step</div>
          <div className="text-white text-sm">{screen.suggested_next_step}</div>
        </div>
      )}
    </div>
  );
}


/**
 * Modal that composes a Calendly booking link pre-filled with the candidate's
 * name + email, lets the recruiter add an optional message, and fires the
 * backend endpoint that (a) emails the candidate and (b) flips status to
 * "interviewing" in one call.
 */
function ScheduleInterviewModal({ application, token, onDone, onClose }) {
  const [calendlyBase, setCalendlyBase] = useState(
    process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/wisewit/discovery",
  );
  const [message, setMessage] = useState(
    `Hi ${(application.full_name || "").split(" ")[0] || "there"} — thanks for applying. Pick a slot that works for you and we'll take it from there.`,
  );
  const [sending, setSending] = useState(false);

  const previewUrl = (() => {
    try {
      const u = new URL(calendlyBase);
      u.searchParams.set("name", application.full_name || "");
      u.searchParams.set("email", application.email || "");
      return u.toString();
    } catch { return calendlyBase; }
  })();

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/applications/${application.id}/schedule-interview`,
        { calendly_url: calendlyBase, message },
        { headers: { "X-Admin-Token": token } },
      );
      toast.success("Interview invite emailed. Status → interviewing.");
      onDone(data.status || "interviewing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(previewUrl); toast.success("Link copied."); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };

  return (
    <div className="fixed inset-0 z-[65] bg-[#050505]/85 backdrop-blur flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={onClose} data-testid="schedule-modal">
      <div className="max-w-xl w-full bg-[#0a0a0a] border border-[#2A2A2A]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00FFB2]">◢ Schedule interview</div>
            <h3 className="mt-1 font-display text-xl text-white">{application.full_name}</h3>
            <div className="font-mono text-[11px] text-[#8E8E93]">{application.email} · {application.position_title}</div>
          </div>
          <button onClick={onClose} className="text-[#8E8E93] hover:text-white" data-testid="schedule-close"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="ww-label">Calendly booking URL</div>
            <input
              value={calendlyBase}
              onChange={(e) => setCalendlyBase(e.target.value)}
              className="ww-input font-mono text-xs"
              data-testid="schedule-url"
            />
            <div className="mt-2 font-mono text-[10px] text-[#8E8E93] break-all">
              → will be sent as: <span className="text-[#DFFF00]">{previewUrl}</span>
            </div>
          </label>

          <label className="block">
            <div className="ww-label">Personal note (optional)</div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="ww-input"
              data-testid="schedule-message"
            />
          </label>

          <div className="pt-3 border-t border-[#2A2A2A] flex flex-wrap gap-2 justify-end">
            <button onClick={copyLink} className="btn-ghost" data-testid="schedule-copy"><ArrowSquareOut size={14} /> Copy link only</button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={send} disabled={sending} className="btn-primary" data-testid="schedule-send">
              <Calendar size={14} /> {sending ? "Sending…" : "Send invite & mark interviewing"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
